import React, { useEffect } from "react";
import { Phone, Mail } from "lucide-react";
import SubpageHero from "@/components/SubpageHero";
import SubpageLayout from "@/components/SubpageLayout";
import SectionNumber from "@/components/SectionNumber";
import UpcomingEvents from "@/components/UpcomingEvents";
import PageGallery from "@/components/PageGallery";
import Embers from "@/components/Embers";
import { SITE } from "@/lib/data";
import useScrollReveal from "@/hooks/useScrollReveal";
import { BookingProcess } from "./FalusiVendegasztal";

const PRICES = [
  {
    period: "Januártól – májusig",
    price: "50 000 Ft / éj",
    note: "Reggelivel · min. 3 éjszakára · kivéve ünnepi időszakok.",
  },
  {
    period: "Májustól – júliusig, szeptembertől – novemberig",
    price: "60 000 Ft / éj",
    note: "Reggelivel · min. 3 éjszakára · kivéve ünnepi időszakok.",
  },
  {
    period: "Júliusban és augusztusban, karácsony, szilveszter, ünnepek",
    price: "70 000 Ft / éj",
    note:
      "Reggelivel · min. 3 éjszakára. Karácsonyi és szilveszteri időszak: dec. 20 – jan. 3.",
  },
  {
    period: "Falusi vendégasztal vacsora",
    price: "20 000 Ft / fő / alkalom",
    note: "Külön szolgáltatás, a vendégház foglaláshoz kapcsolható.",
  },
];

export default function Vendeghaz() {
  useScrollReveal();
  useEffect(() => {
    document.title = "Vendégház · Pakkaland";
  }, []);

  return (
    <SubpageLayout testId="page-vendeghaz">
      <SubpageHero
        crumb="Vendégház"
        eyebrow="Szálláshely · Vezseny"
        title="Vendégház"
        tagline="Fedezd fel a nyugalom szigetét Vezsenyen, a Tisza közelében."
        image="/images/f7m9bya5.jpg"
      />

      {/* Intro */}
      <section className="relative bg-white py-24 sm:py-32 overflow-hidden">
        <SectionNumber n="01" />
        <Embers count={10} variant="light" />
        <div className="max-w-5xl mx-auto px-5 sm:px-8 relative">
          <div className="reveal">
            <span className="eyebrow">Nyugalom</span>
            <p className="font-serif text-2xl sm:text-3xl lg:text-[2.1rem] leading-[1.4] text-pkcharcoal mt-6 font-light">
              Ha csendre, zavartalan elvonulásra és egy csipetnyi
              exkluzivitásra vágysz távol a város zajától, de mégis
              karnyújtásnyira a természettől, akkor{" "}
              <span className="italic text-pkred">megtaláltad a tökéletes helyet.</span>
            </p>
          </div>
        </div>
      </section>

      {/* Three feature blocks */}
      <FeatureBlock
        n="02"
        eyebrow="Szálláshely"
        title="Otthonos kényelem"
        text="Igényesen kialakított, egyhálós és nappalis vendégházunkban 4 fő fér el teljes kényelemben. A konyhát úgy szereltük fel, hogy minden a kezed ügyében legyen – érezd magad otthon az első perctől kezdve."
        img="/images/rjkboggp.jpg"
        reversed={false}
        bg="bg-pkbeige"
        tags={["4 fő", "Egyhálós + nappali", "Felszerelt konyha"]}
        badge={{ k: "Kapacitás", v: "4 fő" }}
      />
      <FeatureBlock
        n="03"
        eyebrow="Élmény"
        title="Wellness és kulináris élvezetek"
        text="A tökéletes lazításról saját medence, jakuzzi és egy hangulatos dézsafürdő gondoskodik. Ha pedig megéheztek, a szabadtéri élményeket a grillező, a bogrács és a kemence teszi teljessé. Nem szeretnél főzni? Falusi vendégasztalunkon házias reggelivel, bőséges vacsorával és kemencében sült finomságokkal kényeztetünk."
        img="/images/xo022efk.jpg"
        reversed
        bg="bg-white"
        tags={["Medence", "Jakuzzi", "Dézsa", "Kemence"]}
        badge={{ k: "Hangulat", v: "Dézsafürdő" }}
      />
      <FeatureBlock
        n="04"
        eyebrow="Állatbarát"
        title="Különleges társaság"
        text="A vendégház körül barátságos állatok – kíváncsi alpakák, gyönyörű lovak és kedves kutyák – teszik még varázslatosabbá az itt töltött időt. Ráadásul a négylábú kedvenceiteket sem kell otthon hagynotok: kutyabarát szállás vagyunk, így a család tényleg minden tagja együtt pihenhet."
        img="/images/11wfj7do.jpg"
        reversed={false}
        bg="bg-pkbeige"
        ctaLabel="Foglalj most és hozd a kutyádat is"
        tags={["Alpakák", "Lovak", "Kutyabarát szállás"]}
        badge={{ k: "Család", v: "Pet friendly" }}
      />

      {/* Pricing */}
      <section className="relative bg-pkcharcoal py-24 sm:py-32 overflow-hidden">
        <SectionNumber n="05" variant="dark" />
        <div
          className="absolute inset-0 pointer-events-none opacity-25"
          style={{
            background:
              "radial-gradient(ellipse at 20% 70%, rgba(217,4,41,0.35) 0%, transparent 55%)",
          }}
        />
        <Embers count={12} />
        <div className="relative max-w-5xl mx-auto px-5 sm:px-8">
          <div className="text-center mb-14 reveal">
            <span className="eyebrow center">Árak</span>
            <h2 className="font-serif text-white text-4xl sm:text-5xl lg:text-6xl mt-6 leading-[1.05]">
              Foglalás &<br />
              <span className="italic text-pkred">vendéglátás.</span>
            </h2>
          </div>
          <ul className="divide-y divide-white/10 border-y border-white/10">
            {PRICES.map((p, i) => (
              <li
                key={p.period}
                data-testid={`price-row-${i}`}
                className="py-6 sm:py-8 flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-8 reveal"
                style={{ transitionDelay: `${i * 60}ms` }}
              >
                <div className="flex-1">
                  <div className="font-serif text-white text-xl sm:text-2xl">
                    {p.period}
                  </div>
                  <div className="text-white/55 text-sm mt-1">{p.note}</div>
                </div>
                <div className="text-pkred font-serif text-2xl sm:text-3xl font-medium shrink-0">
                  {p.price}
                </div>
              </li>
            ))}
          </ul>
          <div className="mt-12 flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href={SITE.phoneHref}
              data-testid="vendeghaz-cta-phone"
              className="btn-fire"
            >
              <Phone className="w-4 h-4" strokeWidth={1.8} />
              Vendégház foglalás
            </a>
            <a
              href={SITE.emailHref}
              data-testid="vendeghaz-cta-email"
              className="btn-ghost"
            >
              <Mail className="w-4 h-4" strokeWidth={1.8} />
              Írj nekünk
            </a>
          </div>
        </div>
      </section>

      <BookingProcess sectionN="08" />
    </SubpageLayout>
  );
}

function FeatureBlock({ n, eyebrow, title, text, img, reversed, bg, ctaLabel, tags, badge }) {
  return (
    <section className={`relative ${bg} py-24 sm:py-32 overflow-hidden`}>
      <SectionNumber n={n} />

      {/* Warm radial blob — alternates side based on layout */}
      <div
        aria-hidden="true"
        className={`absolute ${
          reversed ? "-bottom-32 -left-32" : "-bottom-32 -right-32"
        } w-[460px] h-[460px] rounded-full opacity-[0.05] blur-3xl pointer-events-none`}
        style={{
          background:
            "radial-gradient(circle, #D90429 0%, #FFA64D 55%, transparent 75%)",
        }}
      />

      <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-12 lg:gap-20 items-center relative">
        {/* TEXT side */}
        <div
          className={`lg:col-span-5 reveal ${
            reversed ? "lg:order-2" : "lg:order-1"
          }`}
        >
          {/* Eyebrow + step indicator */}
          <div className="flex items-center gap-4">
            <span className="eyebrow">{eyebrow}</span>
          </div>

          <h2 className="font-serif text-pkcharcoal text-4xl sm:text-5xl lg:text-[3.5rem] mt-6 leading-[1.02]">
            {title.split(" ").slice(0, -1).join(" ")}{" "}
            <span className="italic text-pkred">
              {title.split(" ").slice(-1)}
            </span>
          </h2>

          {/* Decorative line */}
          <div className="mt-6 flex items-center gap-3">
            <span className="block w-12 h-px bg-pkred" />
            <span className="block w-1.5 h-1.5 rounded-full bg-pkred shadow-[0_0_10px_rgba(217,4,41,0.7)]" />
          </div>

          <p className="mt-8 text-pkcharcoal/75 text-base sm:text-lg leading-relaxed">
            {text}
          </p>

          {/* Feature tags */}
          {tags && tags.length > 0 && (
            <div className="mt-8 flex flex-wrap gap-2">
              {tags.map((t) => (
                <span
                  key={t}
                  className="px-3 py-1.5 bg-white border border-pkash text-[0.7rem] tracking-[0.15em] uppercase text-pkcharcoal/75 font-semibold hover:border-pkred hover:text-pkred transition-colors cursor-default"
                >
                  {t}
                </span>
              ))}
            </div>
          )}

          {ctaLabel && (
            <a href={SITE.phoneHref} className="btn-fire mt-10">
              {ctaLabel}
            </a>
          )}
        </div>

        {/* IMAGE side */}
        <div
          className={`lg:col-span-7 reveal ${
            reversed ? "lg:order-1" : "lg:order-2"
          }`}
        >
          <div className="relative">
            {/* Offset frame behind image */}
            <div
              aria-hidden="true"
              className={`hidden sm:block absolute ${
                reversed
                  ? "-top-5 -right-5"
                  : "-top-5 -left-5"
              } w-full h-full border border-pkred/30 pointer-events-none`}
            />

            <div className="relative aspect-[5/4] overflow-hidden bg-pkcharcoal">
              <img
                src={img}
                alt={title}
                className="absolute inset-0 w-full h-full object-cover hover:scale-105 transition-transform duration-1000"
              />

              {/* Floating badge on image */}
              {badge && (
                <div
                  className={`absolute ${
                    reversed ? "bottom-5 left-5" : "bottom-5 right-5"
                  } bg-white/95 backdrop-blur-sm px-4 py-3 shadow-2xl flex items-center gap-3 max-w-xs`}
                >
                  <span className="block w-1 h-10 bg-pkred shrink-0" />
                  <div>
                    <div className="text-[0.55rem] tracking-[0.3em] uppercase text-pkcharcoal/50 font-semibold">
                      {badge.k}
                    </div>
                    <div className="font-serif text-pkcharcoal text-lg leading-tight mt-0.5">
                      {badge.v}
                    </div>
                  </div>
                </div>
              )}

              {/* Number indicator on image opposite corner */}
              <div
                className={`absolute top-4 ${
                  reversed ? "right-4" : "left-4"
                } text-white/30 font-serif text-6xl leading-none font-extralight select-none pointer-events-none`}
                style={{
                  WebkitTextStroke: "1px rgba(255,255,255,0.4)",
                  color: "transparent",
                }}
              >
                {n}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
